import { IAppContext } from 'App/AppContext';
import { TFunction } from 'i18next';
import { IHubConnection, IHubMethod, startHubConnection } from './GeneralHub';

export const ReceiveMessage = 'ReceiveMessage';
export const ReceiveStatus = 'ReceiveStatus';
export const ReceiveContext = 'ReceiveContext';
export const ReceiveAgentEvent = 'ReceiveAgentEvent';

export type CopilotHubMethodReceiveMessage = (msg: string) => void;
export type CopilotHubMethodReceiveStatus = (msg: CopilotStatus) => void;
export type CopilotHubMethodReceiveContext = (context: string, type: CopilotConversationContext) => void;
export type CopilotHubMethodReceiveAgentEvent = (event: CopilotAgentEvent) => void;

export const CopilotHubEndpoint = 'hubs/copilot';

export enum CopilotHubMessageType {
  Chat = 0,
  Document = 1,
}

export enum CopilotAgentEventKind {
  TextDelta = 0,
  ToolResult = 2,
  Trace = 3,
  Done = 4,
  Error = 5,
  PlanUpdated = 6,
  // Backend -> frontend: run a command against the live editor mounted in the browser.
  EditorToolCommand = 10,
}

// Client -> server hub methods used by the document editors to answer editor commands and persist final state.
export const SendEditorToolResult = 'SendEditorToolResult';
export const NotifyDocumentState = 'NotifyDocumentState';

export interface CopilotTaskTodoUpdate {
  id?: string;
  title?: string;
  description?: string;
  kind?: string;
  status?: string;
  toolName?: string;
  requiresHumanApproval?: boolean;
  expectedOutputType?: string;
}

export interface CopilotTaskPlanUpdate {
  runId?: string;
  status?: string;
  rationale?: string;
  reviewRationale?: string;
  reviewIssues?: string[];
  planJson?: string;
  todos?: CopilotTaskTodoUpdate[];
}

export const isCopilotTaskPlanUpdate = (payload: unknown): payload is CopilotTaskPlanUpdate => {
  if (!payload || typeof payload !== 'object') return false;

  const plan = payload as Partial<CopilotTaskPlanUpdate>;

  return Array.isArray(plan.todos);
};

export interface CopilotAgentEvent {
  runId?: string;
  sequence?: number;
  parentId?: string;
  phase?: string;
  iteration?: number;
  status?: string;
  clientSequence?: number;
  kind: CopilotAgentEventKind;
  text?: string;
  messageType?: CopilotHubMessageType;
  callId?: string;
  toolName?: string;
  argumentsJson?: string;
  resultPreview?: string;
  payload?: unknown;
  traceName?: string;
  occurredAt?: string;
  errorCode?: string;
  message?: string;
}

export enum CopilotStatus {
  Preparing = 0,
  GatheringData = 1,
  CreatingTasks = 2,
  Thinking = 3,
  Categorizing = 4,
  Linking = 5,
  RemovePreview = 6,
  BuildingLibrary = 7,
  CreatingProcesses = 8,
}

export enum CopilotConversationContext {
  UserId = 0,
  Date = 1,
}

export const copilotStatusMessages = (t: TFunction<string>): Record<CopilotStatus, string> => ({
  [CopilotStatus.Preparing]: t('copilot:StatusMessage.Preparing'),
  [CopilotStatus.GatheringData]: t('copilot:StatusMessage.GatheringData'),
  [CopilotStatus.CreatingTasks]: t('copilot:StatusMessage.CreatingTasks'),
  [CopilotStatus.Thinking]: t('copilot:StatusMessage.Thinking'),
  [CopilotStatus.Categorizing]: t('copilot:StatusMessage.Categorizing'),
  [CopilotStatus.Linking]: t('copilot:StatusMessage.Linking'),
  [CopilotStatus.RemovePreview]: t('copilot:StatusMessage.RemovingPreview'),
  [CopilotStatus.BuildingLibrary]: t('copilot:StatusMessage.BuildingLibrary'),
  [CopilotStatus.CreatingProcesses]: t('copilot:StatusMessage.CreatingProcesses'),
});

export const startCopilotHubConnection = async (
  appContext: IAppContext,
  agentEventCallback: CopilotHubMethodReceiveAgentEvent,
): Promise<IHubConnection | null> => {
  return startHubConnection(appContext, CopilotHubEndpoint, [
    { method: ReceiveAgentEvent, callback: agentEventCallback },
  ]);
};

export const startCopilotChatHubConnection = async (
  appContext: IAppContext,
  messageCallback: CopilotHubMethodReceiveMessage,
  statusCallback: CopilotHubMethodReceiveStatus,
  contextCallback: CopilotHubMethodReceiveContext,
  agentEventCallback: CopilotHubMethodReceiveAgentEvent,
): Promise<IHubConnection | null> => {
  const methods: IHubMethod[] = [
    { method: ReceiveMessage, callback: messageCallback },
    { method: ReceiveStatus, callback: statusCallback },
    { method: ReceiveContext, callback: contextCallback },
    { method: ReceiveAgentEvent, callback: agentEventCallback },
  ];

  return startHubConnection(appContext, CopilotHubEndpoint, methods);
};
